import React from 'react';
import { LoaderCircle } from 'lucide-react';
import { cn } from '../../lib/utils';
import {
  cardClass,
  primaryText,
  secondaryText,
  tertiaryText,
  largeTextSize,
  semiBoldText,
  smallTextSize,
  mediumText,
} from '../../styling/stylingHelper';

export interface SpendingSplitSegment {
  /** Already-translated label (Food / Drinks / Non-Food). */
  label: string;
  /** Share of total spend, 0–100. */
  pct: number;
  color: string;
}

interface SpendingSplitBarProps {
  title: string;
  segments: SpendingSplitSegment[];
  loading?: boolean;
  emptyText?: string;
}

/**
 * Static food/drinks/non-food spending split as a single segmented bar with a
 * legend underneath. Pure presentation — the caller computes the segments (e.g.
 * from `calculateSpendingSplit`) and translates the labels.
 */
export const SpendingSplitBar: React.FC<SpendingSplitBarProps> = ({
  title,
  segments,
  loading,
  emptyText,
}) => {
  return (
    <div className={cn(cardClass, 'px-4 pt-4 pb-5')}>
      <h2 className={cn(primaryText, largeTextSize, semiBoldText, 'mb-4')}>{title}</h2>

      {loading ? (
        <div className="flex items-center justify-center py-6">
          <LoaderCircle className={cn('h-6 w-6 animate-spin', primaryText)} />
        </div>
      ) : segments.length === 0 ? (
        <p className={cn(tertiaryText, smallTextSize, 'py-4')}>{emptyText}</p>
      ) : (
        <>
          {/* Segmented bar */}
          <div className="flex w-full h-[24px] rounded-[6px] overflow-hidden mb-3">
            {segments.map((s) => (
              <div
                key={s.label}
                className="h-full first:rounded-l-[6px] last:rounded-r-[6px]"
                style={{ width: `${s.pct}%`, backgroundColor: s.color }}
              />
            ))}
          </div>

          {/* Legend */}
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
            {segments.map((s) => (
              <div key={s.label} className="flex items-center gap-1.5">
                <span
                  className="w-2.5 h-2.5 rounded-[3px] flex-shrink-0"
                  style={{ backgroundColor: s.color }}
                />
                <span className={cn(secondaryText, smallTextSize, mediumText)}>
                  {s.label}{' '}
                  <span className={cn(primaryText, semiBoldText)}>{s.pct.toFixed(1)}%</span>
                </span>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};
